import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldWarning, Warning, CaretRight } from "@phosphor-icons/react";
import { mApi } from "@/lib/api";
import { MERCHANT } from "@/lib/routes";

const COLOR_BY_LEVEL = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-rose-500",
};
const TEXT_BY_LEVEL = {
  green: "text-emerald-700",
  yellow: "text-amber-700",
  red: "text-rose-700",
};
const BG_BY_LEVEL = {
  green: "bg-emerald-50 border-emerald-200",
  yellow: "bg-amber-50 border-amber-200",
  red: "bg-rose-50 border-rose-200",
};
const ICON_BY_LEVEL = {
  green: ShieldCheck,
  yellow: ShieldWarning,
  red: Warning,
};

function relTime(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

export default function SecurityPosture() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    mApi.get("/merchant/security-posture")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load posture"));
  }, []);

  if (error) {
    return (
      <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="security-posture-error">
        <div className="font-display text-xl tracking-tight font-medium">Security posture</div>
        <div className="text-sm text-zinc-500 mt-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="security-posture-loading">
        <div className="font-display text-xl tracking-tight font-medium">Security posture</div>
        <div className="h-4 mt-3 w-32 bg-zinc-100 rounded-sm animate-pulse" />
      </div>
    );
  }

  const Icon = ICON_BY_LEVEL[data.verdict];
  const greens = data.checks.filter((c) => c.level === "green").length;
  const firstFailing = data.checks.find((c) => c.level !== "green");
  return (
    <div
      className={`border rounded-sm p-5 ${BG_BY_LEVEL[data.verdict]}`}
      data-testid="security-posture"
    >
      {data.degraded && (
        <div
          className="mb-4 -mt-1 -mx-1 px-3 py-2 rounded-sm bg-rose-100 border border-rose-200 flex items-center gap-2"
          data-testid="posture-degraded-banner"
        >
          <Warning size={14} className="text-rose-700 shrink-0" />
          <span className="text-xs text-rose-800 flex-1">
            Posture degraded {data.transitioned_from && (<>from <strong>{data.transitioned_from}</strong></>)} → <strong>{data.verdict}</strong>.
            {firstFailing && <> Start with: <em>{firstFailing.label}</em>.</>}
          </span>
          <Link
            to={MERCHANT.security}
            data-testid="posture-fix-now"
            className="text-xs font-medium text-rose-900 underline-offset-2 hover:underline"
          >
            Fix now
          </Link>
        </div>
      )}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-sm bg-white border ${BG_BY_LEVEL[data.verdict]} flex items-center justify-center`}>
            <Icon size={18} className={TEXT_BY_LEVEL[data.verdict]} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Security posture</div>
            <div className={`font-display text-xl tracking-tight font-medium ${TEXT_BY_LEVEL[data.verdict]}`}>
              {data.verdict === "green" ? "Healthy" : data.verdict === "yellow" ? "Action recommended" : "Action required"}
            </div>
          </div>
        </div>
        <div className="text-xs text-zinc-500 tabular-nums">{greens}/{data.checks.length} ok</div>
      </div>

      <ul className="space-y-2.5" data-testid="security-posture-checks">
        {data.checks.map((c) => (
          <li
            key={c.key}
            className="flex items-start gap-3 bg-white/60 border border-white rounded-sm p-2.5"
            data-testid={`posture-check-${c.key}`}
          >
            <span className={`mt-1.5 h-2 w-2 rounded-full ${COLOR_BY_LEVEL[c.level]}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{c.label}</div>
              <div className="text-xs text-zinc-600 truncate">{c.detail}</div>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-4 pt-3 border-t border-white grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-zinc-500">Last login</div>
          <div className="text-zinc-900 tabular-nums">{relTime(data.metrics.last_login_ts)}</div>
        </div>
        <div>
          <div className="text-zinc-500">Last API usage</div>
          <div className="text-zinc-900 tabular-nums">{relTime(data.metrics.last_api_usage_ts)}</div>
        </div>
        <div>
          <div className="text-zinc-500">Active API keys</div>
          <div className="text-zinc-900 tabular-nums">{data.metrics.active_api_keys}</div>
        </div>
        <div>
          <div className="text-zinc-500">Failed logins (24h)</div>
          <div className="text-zinc-900 tabular-nums">{data.metrics.failed_logins_24h}</div>
        </div>
      </div>

      <Link
        to={MERCHANT.security}
        className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-zinc-900 hover:underline"
        data-testid="posture-review-link"
      >
        Review security settings <CaretRight size={11} />
      </Link>
    </div>
  );
}
